/*
 * File: ResetPassword.tsx
 * Project: a-a
 * File Created: Wednesday, 23rd October 2024 10:09:05 pm
 * Author: Jackson Thounaojam (jackson.thounaojam@mutualmobile.com)
 * -----
 * Last Modified: Wednesday, 23rd July 2025 10:16:21 pm
 * Modified By: Jackson Thounaojam (jackson.thounaojam@mutualmobile.com)
 * -----
 * Copyright 2020 - 2024 Mutual Mobile, Mutual Mobile
 */
import React, { useEffect, useState } from 'react';
import EntryLayoutScreen from '../../shared/EntryLayout/EntryLayout';

import styles from './ResetPasswordForm.module.css';

import CustomButton from '../../shared/CustomButton/CustomButton';
import zeplinColor from '../../../theme/Color';
import CustomPassword from '../../shared/CustomPassword/CustomPassword';
import { useInput } from '../../../customHook/useInput';
import { validatePassword } from '../../../helper/validate';
import { Typography } from '@mui/material';
import { AuthService } from '../../../service/auth.service';
import { Constants, Error } from '../../../utilities/constants';

type ResetPasswordFormProps = {
  user: string;
  otp: string;
  setNext: React.Dispatch<React.SetStateAction<string>>;
};

const ResetPasswordForm = ({ user, otp, setNext }: ResetPasswordFormProps) => {
  // sessionStorage.removeItem('otp')
  const [mismatch, setMismatch] = useState(false);
  const [alert, setAlert] = useState('');
  const [error, setError] = useState(false);
  const {
    handleChange: handlePasswordChange,
    handleBlur: handlePasswordBlur,
    value: password,
    invalid: invalidPassword,
    didChange: didPasswordChange,
  } = useInput('', (text) => validatePassword(text).valid);
  const [showPassword, setShowPassword] = useState(false);
  const {
    handleChange: handleConfirmPasswordChange,
    handleBlur: handleConfirmPasswordBlur,
    value: confirmPassword,
    invalid: invalidConfirmPassword,
    didChange: didConfirmPasswordChange,
  } = useInput('', (text) => validatePassword(text).valid);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [check, setCheck] = useState<{
    valid: boolean;
    hasLowercase: boolean;
    hasUppercase: boolean;
    hasMinLength: boolean;
    hasSymbol: boolean;
    hasNumber: boolean;
  }>({
    valid: false,
    hasLowercase: false,
    hasUppercase: false,
    hasMinLength: false,
    hasSymbol: false,
    hasNumber: false,
  });
  useEffect(() => {
    // check = validatePassword(password);
    setCheck(validatePassword(password));
  }, [password]);

  const handleClickShowPassword = () => setShowPassword((show) => !show);

  const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  const handleMouseUpPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };
  const handleClickShowConfirmPassword = () => setShowConfirmPassword((show) => !show);

  const handleMouseDownConfirmPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  const handleMouseUpConfirmPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };
  const valid = confirmPassword?.length && password?.length;
  const resetPassword = async () => {
    try {
      if (confirmPassword === password) {
        const res = await AuthService.setPassword({
          userName: user,
          confirmationCode: Number(otp),
          password: password,
        });
        console.log(res);
        if (res?.$metadata?.httpStatusCode === 200) {
          sessionStorage.setItem('next', 'confirm');
          setNext('confirm');
        }
      } else {
        setMismatch(true);
      }
    } catch (err: any) {
      setError(true);
      const message: Error = err?.response?.data?.name || '';
      // if(err?.response?.data?.name===Error.ExpiredCode){
      console.log(message);
      setAlert(Constants.ErrorText[message]);
      // }
    }
  };
  const children = (
    <div className={styles['reset-field-input']}>
      <CustomPassword
        style={{ width: '50%', margin: 'auto' }}
        showPassword={showPassword}
        handleClickShowPassword={handleClickShowPassword}
        handleMouseDownPassword={handleMouseDownPassword}
        handleMouseUpPassword={handleMouseUpPassword}
        onBlur={handlePasswordBlur}
        onChange={handlePasswordChange}
        name='password'
        text={password}
        error={invalidPassword && didPasswordChange ? 'Create valid password' : ''}
        label='New password'
      />

      <div className={styles['password-rule']} style={{ color: zeplinColor.Neutral9 }}>
        <div className={styles['validation-points']}>
          <img
            src={check?.hasMinLength ? '/assets/check.svg' : '/assets/dot.svg'}
            width={20}
            height={20}
          />
          <Typography variant='body3' style={{ color: zeplinColor.Neutral9 }}>
            At least 8 characters
          </Typography>
        </div>
        <div className={styles['validation-points']}>
          <img
            src={check?.hasNumber ? '/assets/check.svg' : '/assets/dot.svg'}
            width={20}
            height={20}
          />
          <Typography variant='body3' style={{ color: zeplinColor.Neutral9 }}>
            At least 1 number
          </Typography>
        </div>
        <div className={styles['validation-points']}>
          <img
            src={
              check?.hasLowercase && check?.hasUppercase ? '/assets/check.svg' : '/assets/dot.svg'
            }
            width={20}
            height={20}
          />
          <Typography variant='body3' style={{ color: zeplinColor.Neutral9 }}>
            At least 1 uppercase & 1 lowercase letter
          </Typography>
        </div>
        <div className={styles['validation-points']}>
          <img
            src={check?.hasSymbol ? '/assets/check.svg' : '/assets/dot.svg'}
            width={20}
            height={20}
          />
          <Typography variant='body3' style={{ color: zeplinColor.Neutral9 }}>
            At least 1 special character
          </Typography>
        </div>
      </div>
      <CustomPassword
        style={{ width: '50%', margin: 'auto' }}
        showPassword={showConfirmPassword}
        handleClickShowPassword={handleClickShowConfirmPassword}
        handleMouseDownPassword={handleMouseDownConfirmPassword}
        handleMouseUpPassword={handleMouseUpConfirmPassword}
        onBlur={handleConfirmPasswordBlur}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          handleConfirmPasswordChange(e);
          setMismatch(false);
        }}
        name='confirmPassword'
        text={confirmPassword}
        error={mismatch ? 'Password must match' : ''}
        label='Confirm new password'
      />
      <CustomButton
        onClick={resetPassword}
        variant='contained'
        disabled={valid ? false : true}
        color={valid ? 'primary' : 'customDisable'}
        style={{ width: '50%', borderRadius: '14px', height: 56 }}
        textStyle={{ color: valid ? zeplinColor.Neutral1 : zeplinColor.Neutral8 }}
        text={'Reset Password'}
      />
    </div>
  );

  return (
    <EntryLayoutScreen
      header={'Create a new password'}
      subheader={`Almost done. Enter your new password and you're good to go.`}
      input={children}
      alternative={''}
      footerText=''
      footerButtonText=''
      icon={
        <div
          style={{
            backgroundColor: zeplinColor.Neutral3,
            borderRadius: '70px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            margin: 'auto',
            width: '80px',
            height: '80px',
            padding: '20px',
          }}
        >
          <img src={'/assets/reset-password.svg'} width={40} height={40} />
        </div>
      }
      clickAction={() => {}}
      alert={alert}
      error={error}
    />
  );
};

export default ResetPasswordForm;
